import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { AutoAwesomeRounded, SaveRounded } from '@mui/icons-material';
import type { AcademicLevel, CapCode } from '@dosfilos/domain';
import { db } from '../../lib/firebase';
import { useTenantStore } from '../../stores/tenant.store';
import { capApi, type ProgramOutcomeDto } from '../../lib/api/cap.api';
import { useProgramCapTarget } from '../../hooks/use-program-cap-target';
import { CapPageHeader, CAP_CONTENT_MAX_WIDTH } from './components/CapPageHeader';
import { errorText } from '../../lib/error-text';

// ─── Program Outcomes (PLOs) editor — Phase E CAP, PR3 ─────────────────────
// Seeds the official TMAI PLOs for a program+CAP (only-if-empty, server-side)
// and lets the registrar adjust wording per level/language. Reads go straight
// to Firestore (rules: staff read); every mutation goes through the guarded
// callables. No add/delete — the official set IS the standard.

interface OutcomeFormRow {
  dto: ProgramOutcomeDto;
  ugEs: string;
  ugEn: string;
  gradEs: string;
  gradEn: string;
  /** Which face of the card is shown/edited — a view flag, not persisted. */
  lang: 'es' | 'en';
  dirty: boolean;
  saving: boolean;
}

const toRow = (dto: ProgramOutcomeDto): OutcomeFormRow => ({
  dto,
  ugEs: dto.undergraduate.es,
  ugEn: dto.undergraduate.en,
  gradEs: dto.graduate.es,
  gradEn: dto.graduate.en,
  lang: 'es',
  dirty: false,
  saving: false,
});

// The four editable fields keyed by level + language. The page-level level
// selector + the per-card ES/EN toggle pick which one is shown; the row keeps
// all four so a save persists both levels (only the visible one is edited).
type OutcomeFieldKey = 'ugEs' | 'ugEn' | 'gradEs' | 'gradEn';
const fieldKey = (level: AcademicLevel, lang: 'es' | 'en'): OutcomeFieldKey =>
  `${level === 'undergraduate' ? 'ug' : 'grad'}${lang === 'es' ? 'Es' : 'En'}` as OutcomeFieldKey;

export function ProgramOutcomesPage(): JSX.Element {
  const { t } = useTranslation('cap');
  const navigate = useNavigate();
  const { programId } = useParams<{ programId: string }>();
  const { currentTenantId } = useTenantStore();

  const [capCode, setCapCode] = useState<CapCode>('BSC');
  const [level, setLevel] = useState<AcademicLevel>('undergraduate');
  const [rows, setRows] = useState<OutcomeFormRow[]>([]);

  // Default the CAP + level to the program's target so opening this from the
  // journey lands on the right certification instead of BSC (still switchable).
  const capTarget = useProgramCapTarget(programId);
  useEffect(() => {
    if (!capTarget) return;
    setCapCode(capTarget.capCode);
    setLevel(capTarget.level);
  }, [capTarget]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  // Staleness guard: capCode flips (BSC default → program's capTarget) fire two
  // loads; only the latest response may apply, else BSC (empty) can overwrite DEP.
  const loadReq = useRef(0);
  const load = useCallback(async (): Promise<void> => {
    if (!currentTenantId || !programId) return;
    const req = ++loadReq.current;
    setLoading(true);
    setError(null);
    try {
      const snap = await getDocs(
        query(
          collection(db, `tenants/${currentTenantId}/programOutcomes`),
          where('programId', '==', programId),
          where('capCode', '==', capCode),
          orderBy('order'),
        ),
      );
      if (loadReq.current !== req) return;
      setRows(
        snap.docs.map((d) => {
          const data = d.data();
          return toRow({
            id: d.id,
            programId: data['programId'] as string,
            capCode: data['capCode'] as CapCode,
            sourceOutcomeId: data['sourceOutcomeId'] as string,
            undergraduate: data['undergraduate'] as { es: string; en: string },
            graduate: data['graduate'] as { es: string; en: string },
            order: (data['order'] as number) ?? 1,
            updatedAt: '',
          });
        }),
      );
    } catch (e) {
      if (loadReq.current === req) setError(errorText(e));
    } finally {
      if (loadReq.current === req) setLoading(false);
    }
  }, [currentTenantId, programId, capCode]);

  useEffect(() => {
    void load();
  }, [load]);

  const seed = async (): Promise<void> => {
    if (!currentTenantId || !programId) return;
    setSeeding(true);
    setError(null);
    try {
      const res = await capApi.defineOutcomes({
        tenantId: currentTenantId,
        programId,
        capCode,
      });
      setRows(res.outcomes.map(toRow));
      setSavedMsg(
        res.seeded ? t('plos.seeded', { count: res.outcomes.length }) : t('plos.alreadySeeded'),
      );
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSeeding(false);
    }
  };

  const patchRow = (id: string, patch: Partial<OutcomeFormRow>): void => {
    setRows((rs) => rs.map((r) => (r.dto.id === id ? { ...r, ...patch, dirty: true } : r)));
  };

  // Flip a single card's face — a view change, so it must NOT mark the row dirty.
  const setRowLang = (id: string, lang: 'es' | 'en'): void => {
    setRows((rs) => rs.map((r) => (r.dto.id === id ? { ...r, lang } : r)));
  };

  const saveRow = async (row: OutcomeFormRow): Promise<void> => {
    if (!currentTenantId) return;
    setRows((rs) => rs.map((r) => (r.dto.id === row.dto.id ? { ...r, saving: true } : r)));
    setError(null);
    try {
      const res = await capApi.updateOutcome({
        tenantId: currentTenantId,
        outcomeId: row.dto.id,
        undergraduate: { es: row.ugEs.trim(), en: row.ugEn.trim() },
        graduate: { es: row.gradEs.trim(), en: row.gradEn.trim() },
      });
      setRows((rs) => rs.map((r) => (r.dto.id === row.dto.id ? { ...toRow(res.outcome) } : r)));
      setSavedMsg(t('plos.saved', { plo: row.dto.sourceOutcomeId }));
    } catch (e) {
      setRows((rs) => rs.map((r) => (r.dto.id === row.dto.id ? { ...r, saving: false } : r)));
      setError(errorText(e));
    }
  };

  return (
    <Box>
      <CapPageHeader
        backLabel={t('plos.back')}
        onBack={() => navigate(-1)}
        title={t('plos.title')}
        subtitle={t('plos.subtitle')}
      />
      <Box sx={{ px: 3, py: 3, pb: 6, maxWidth: CAP_CONTENT_MAX_WIDTH, mx: 'auto' }}>
        <Stack direction="row" alignItems="center" spacing={2} mb={2} flexWrap="wrap" useFlexGap>
          <ToggleButtonGroup
            exclusive
            value={capCode}
            onChange={(_, v: CapCode | null) => v && setCapCode(v)}
            size="small"
          >
            <ToggleButton value="BSC">{t('plos.caps.BSC')}</ToggleButton>
            <ToggleButton value="DEP">{t('plos.caps.DEP')}</ToggleButton>
          </ToggleButtonGroup>
          <Box flex={1} />
          <Typography variant="body2" color="text.secondary">
            {t('plos.levelLabel')}
          </Typography>
          <ToggleButtonGroup
            exclusive
            value={level}
            onChange={(_, v: AcademicLevel | null) => v && setLevel(v)}
            size="small"
          >
            <ToggleButton value="undergraduate">{t('levels.undergraduate')}</ToggleButton>
            <ToggleButton value="graduate">{t('levels.graduate')}</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ height: 4, mb: 1.5 }}>
          {loading && rows.length > 0 && <LinearProgress sx={{ borderRadius: 4 }} />}
        </Box>

        {loading && rows.length === 0 ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <Typography variant="body1" mb={2}>
              {t('plos.empty')}
            </Typography>
            <Button
              variant="contained"
              startIcon={seeding ? <CircularProgress size={16} /> : <AutoAwesomeRounded />}
              disabled={seeding}
              onClick={() => void seed()}
            >
              {t('plos.seedCta')}
            </Button>
            <Typography variant="caption" display="block" color="text.secondary" mt={1}>
              {t('plos.seedHint')}
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={2}>
            {rows.map((row) => (
              <Paper
                key={row.dto.id}
                variant="outlined"
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  borderLeft: '4px solid',
                  borderLeftColor: row.dirty ? 'warning.main' : 'primary.main',
                  transition: 'box-shadow .15s',
                  '&:hover': { boxShadow: 3 },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} mb={2} flexWrap="wrap">
                  <Chip
                    size="small"
                    label={row.dto.sourceOutcomeId}
                    color="primary"
                    sx={{ fontWeight: 700 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {t('plos.orderLabel', { order: row.dto.order })}
                  </Typography>
                  <Box flex={1} />
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={row.lang}
                    onChange={(_, v: 'es' | 'en' | null) => v && setRowLang(row.dto.id, v)}
                  >
                    <ToggleButton value="es">{t('plos.lang.es')}</ToggleButton>
                    <ToggleButton value="en">{t('plos.lang.en')}</ToggleButton>
                  </ToggleButtonGroup>
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    startIcon={row.saving ? <CircularProgress size={14} /> : <SaveRounded />}
                    disabled={
                      !row.dirty ||
                      row.saving ||
                      !row.ugEs.trim() ||
                      !row.ugEn.trim() ||
                      !row.gradEs.trim() ||
                      !row.gradEn.trim()
                    }
                    onClick={() => void saveRow(row)}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    {t('plos.saveRow')}
                  </Button>
                </Stack>
                {(() => {
                  const fk = fieldKey(level, row.lang);
                  return (
                    <TextField
                      label={t(`plos.fields.${fk}`)}
                      value={row[fk]}
                      onChange={(e) => patchRow(row.dto.id, { [fk]: e.target.value })}
                      multiline
                      minRows={2}
                      fullWidth
                      helperText={
                        row.lang === 'en' ? t('plos.enAuthoritative') : t('plos.esWorking')
                      }
                    />
                  );
                })()}
              </Paper>
            ))}
          </Stack>
        )}

        <Snackbar
          open={savedMsg !== null}
          autoHideDuration={3500}
          onClose={() => setSavedMsg(null)}
          message={savedMsg ?? ''}
        />
      </Box>
    </Box>
  );
}
